import {makeAutoObservable, runInAction} from 'mobx';
import {networkOptimization} from '../../../../lib/api';
import {DisruptionAnalysisFilterOptions, AppliedDisruptionAnalysisFilter, IDisruptionAnalysisFilterPayload, FetchComplianceResponse} from '../types';
import {isAbortError} from '../../../../lib/error-status';
import {parseFilterValues, joinFilterValues} from '../../components/no-filters/utils/parse-filter-values';

/**
 * Manages disruption analysis filters and data for analyzing network impact when providers are removed.
 * Handles disruption analysis filter options, specialty filters, and roster comparison data.
 */
export class DisruptionStore {
    availableDisruptionAnalysisFilters: DisruptionAnalysisFilterOptions[] = [];
    appliedDisruptionAnalysisFilter: AppliedDisruptionAnalysisFilter = {
        state: '',
        county: '',
        standard_type: '',
        patient_roster_name: '',
        zip: '',
    };
    disruptionAnalysisSpecialtyFilter = '';
    rosterComparisonDisruptionAnalysisData: any = null;
    disruptionAnalysisComplianceScores: FetchComplianceResponse = [];
    loadingDisruptionAnalysisData = false;
    loadingDisruptionAnalysisComparisonData = false;
    loadingDisruptionAnalysisSpecialtyData = false;
    private _selectedRosters: Array<{rosterId: string, revisionId: string}> = [];
    private currentFilterController: AbortController | null = null;
    private currentComparisonController: AbortController | null = null;

    constructor() {
        makeAutoObservable(this);
    }

    get selectedRosters(): Array<{rosterId: string, revisionId: string}> {
        return this._selectedRosters;
    }

    set selectedRosters(value: Array<{rosterId: string, revisionId: string}>) {
        this._selectedRosters = value;
    }

    async loadDisruptiveAnalysisFilterData(runId: number, controller: AbortController): Promise<void> {
        runInAction(() => {
            this.currentFilterController = controller;
            this.loadingDisruptionAnalysisData = true;
        });

        try {
            const response = await networkOptimization.fetchDisruptiveAnalysisFilters(runId, controller.signal);
            runInAction(() => {
                this.availableDisruptionAnalysisFilters = response || [];
            });
        } finally {
            runInAction(() => {
                // Only reset loading if this request is still the current one
                if (this.currentFilterController === controller) {
                    this.loadingDisruptionAnalysisData = false;
                }
            });
        }
    }

    async loadDisruptionAnalysisComparison(
        filters: Record<string, string>,
        runId: number,
        controller: AbortController
    ): Promise<void> {
        runInAction(() => {
            this.currentComparisonController = controller;
            this.loadingDisruptionAnalysisComparisonData = true;
        });

        try {
            const response = await networkOptimization.fetchDisruptionAnalysisComparison(
                filters,
                runId,
                controller.signal
            );
            runInAction(() => {
                this.rosterComparisonDisruptionAnalysisData = response;
            });
        } finally {
            runInAction(() => {
                // Only reset loading if this request is still the current one
                if (this.currentComparisonController === controller) {
                    this.loadingDisruptionAnalysisComparisonData = false;
                }
            });
        }
    }

    /**
     * Set disruption analysis filter with cascading defaults
     */
    setDisruptionAnalysisFilter(filterName: keyof AppliedDisruptionAnalysisFilter, value: string): void {
        if (this.appliedDisruptionAnalysisFilter[filterName] !== value) {
            switch (filterName) {
            case 'standard_type': {
                const defaultPatientRosterName = this.getUniqueValuesByKey(
                    'patient_roster_name',
                    {standard_type: value}
                )[0] || '';
                this.appliedDisruptionAnalysisFilter = {
                    ...this.appliedDisruptionAnalysisFilter,
                    standard_type: value,
                    patient_roster_name: defaultPatientRosterName,
                    state: '',
                    county: '',
                    zip: ''
                };
                this.disruptionAnalysisSpecialtyFilter = '';
                break;
            }
            case 'patient_roster_name': {
                this.appliedDisruptionAnalysisFilter = {
                    ...this.appliedDisruptionAnalysisFilter,
                    patient_roster_name: value,
                    state: '',
                    county: '',
                    zip: ''
                };
                this.disruptionAnalysisSpecialtyFilter = '';
                break;
            }
            case 'state': {
                this.appliedDisruptionAnalysisFilter = {
                    ...this.appliedDisruptionAnalysisFilter,
                    state: value,
                    county: '',
                    zip: ''
                };
                this.disruptionAnalysisSpecialtyFilter = '';
                break;
            }
            case 'county': {
                this.appliedDisruptionAnalysisFilter = {
                    ...this.appliedDisruptionAnalysisFilter,
                    county: value,
                    zip: ''
                };
                this.disruptionAnalysisSpecialtyFilter = '';
                break;
            }
            case 'zip': {
                this.appliedDisruptionAnalysisFilter = {
                    ...this.appliedDisruptionAnalysisFilter,
                    zip: value
                };
                this.disruptionAnalysisSpecialtyFilter = '';
                break;
            }
            }
        }
    }

    resetDisruptionAnalysisFilter(): void {
        const defaultLineOfBusiness = this.availableDisruptionAnalysisFilters[0]?.standard_type || '';
        const defaultPatientRoster = this.getUniqueValuesByKey(
            'patient_roster_name',
            {standard_type: defaultLineOfBusiness}
        )[0] || '';

        this.appliedDisruptionAnalysisFilter = {
            state: '',
            county: '',
            patient_roster_name: defaultPatientRoster,
            standard_type: defaultLineOfBusiness,
            zip: ''
        };
        this.disruptionAnalysisSpecialtyFilter = '';
    }

    setDisruptionAnalysisSpecialtyFilter(value: string): void {
        this.disruptionAnalysisSpecialtyFilter = value;
    }

    /**
     * Get disruption analysis filters as payload for API calls
     */
    getDisruptionAnalysisFiltersPayload(): IDisruptionAnalysisFilterPayload {
        // Helper function to process pipe-separated values
        const processPipeSeparatedValue = (
            value: string,
            filterArray: DisruptionAnalysisFilterOptions[],
            findFn: (item: DisruptionAnalysisFilterOptions, val: string) => boolean,
            extractFn: (item: DisruptionAnalysisFilterOptions) => string | undefined
        ): string => {
            if (!value) return '';

            if (value.includes('|')) {
                const values = parseFilterValues(value);
                const results = values
                    .map(v => {
                        const found = filterArray.find(item => findFn(item, v));
                        return found ? extractFn(found) : null;
                    })
                    .filter(r => r !== undefined && r !== null && r !== '');
                return joinFilterValues(results as string[]);
            }

            const found = filterArray.find(item => findFn(item, value));
            return found ? (extractFn(found) || '') : '';
        };

        const state = processPipeSeparatedValue(
            this.appliedDisruptionAnalysisFilter.state,
            this.availableDisruptionAnalysisFilters,
            (item, val) => item?.state_additional_info?.state === val,
            (item) => item?.state
        );

        const county = processPipeSeparatedValue(
            this.appliedDisruptionAnalysisFilter.county,
            this.availableDisruptionAnalysisFilters,
            (item, val) => `${item?.county_additional_info?.county_name}-${item.ssacd}` === val,
            (item) => item?.ssacd
        );

        const patient_roster_id = this.availableDisruptionAnalysisFilters.find(
            o => o?.patient_roster_name === this.appliedDisruptionAnalysisFilter.patient_roster_name
        )?.patient_roster_id;

        const standard_type = this.appliedDisruptionAnalysisFilter.standard_type;
        const zip = this.appliedDisruptionAnalysisFilter.zip;

        return {
            state: state || '',
            county: county || '',
            standard_type: standard_type || '',
            patient_roster_id: patient_roster_id || '',
            zip: zip || ''
        };
    }

    /**
     * Get unique values for a filter key with optional filtering
     */
    getUniqueValuesByKey(
        key: string | string[],
        filter?: Record<string, string>
    ): string[] {
        let selectedFilters = this.availableDisruptionAnalysisFilters;

        // Helper to get nested value by path (array of keys)
        const getValueByPath = (obj: any, path: string | string[]) => {
            const keys = Array.isArray(path) ? path : path.split('.');
            return keys.reduce((acc, k) => (acc && acc[k] !== undefined ? acc[k] : undefined), obj);
        };

        // Apply filter if provided
        if (filter && Object.keys(filter).length > 0) {
            selectedFilters = selectedFilters.filter(item =>
                Object.entries(filter).every(([fKey, fVal]) => {
                    // Skip empty filter values
                    if (!fVal || fVal === '') return true;

                    const val = getValueByPath(item, fKey);

                    // Handle pipe-separated values (multiple selections)
                    if (fVal.includes('|')) {
                        const filterValues = parseFilterValues(fVal);
                        return filterValues.includes(val);
                    }

                    return val === fVal;
                })
            );
        }

        const values = selectedFilters
            .map(item => {
                if (Array.isArray(key)) {
                    const parts = key.map(k => getValueByPath(item, k));
                    return parts.every(p => typeof p === 'string') ? parts.join('-') : undefined;
                } else {
                    return getValueByPath(item, key);
                }
            })
            .filter(val => typeof val === 'string');

        // Unique values
        return Array.from(new Set(values));
    }

    async loadDisruptionAnalysisSpecialtiesData(
        runId: number,
        revisionId: number,
        filters: IDisruptionAnalysisFilterPayload,
        specialtyFilterCode: string,
        controller: AbortController
    ): Promise<void> {
        runInAction(() => {
            this.loadingDisruptionAnalysisSpecialtyData = true;
        });

        try {
            const requestFilters = {
                ...filters,
                specialty: specialtyFilterCode
            };
            const result = await networkOptimization.fetchDisruptionAnalysisSpecialtyReport(
                requestFilters,
                runId,
                revisionId,
                controller.signal
            );
            runInAction(() => {
                this.disruptionAnalysisComplianceScores = result;
            });
        } catch (error) {
            if (error instanceof Error && !isAbortError(error)) {
                runInAction(() => {
                    this.disruptionAnalysisComplianceScores = [];
                });
            }
            throw error;
        } finally {
            runInAction(() => {
                this.loadingDisruptionAnalysisSpecialtyData = false;
            });
        }
    }
}
